import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import { AlertCircle, ChevronLeft, ChevronRight, DollarSign, LoaderCircle } from 'lucide-react';
import Layout from '@/components/Layout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { ADMIN_AFFILIATE_EARNINGS, ADMIN_REVERSE_AFFILIATE_EARNING } from '@/queries/queries';
import { deriveEarningDisplayStatus } from '@/lib/affiliates/deriveEarningDisplayStatus';
import { formatCents } from '@/lib/format/currency';
import type { AdminAffiliateEarning } from '@/generated/graphql';

const PAGE_SIZE = 20;
type CommissionKindFilter = 'all' | 'affiliate' | 'producer';

export const AdminAffiliateEarnings = () => {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [kindFilter, setKindFilter] = useState<CommissionKindFilter>('all');
  const [reverseTarget, setReverseTarget] = useState<AdminAffiliateEarning | null>(null);
  const [reverseNote, setReverseNote] = useState('');

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(handle);
  }, [search]);

  const { data, loading, error, refetch } = useQuery<{
    adminAffiliateEarnings: { data: AdminAffiliateEarning[]; total: number };
  }>(ADMIN_AFFILIATE_EARNINGS, {
    variables: {
      search: debouncedSearch || undefined,
      kind: kindFilter === 'all' ? undefined : kindFilter,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    },
    fetchPolicy: 'cache-and-network',
  });

  const [reverseEarning, { loading: reversing }] = useMutation(ADMIN_REVERSE_AFFILIATE_EARNING);

  const rows = data?.adminAffiliateEarnings.data ?? [];
  const total = data?.adminAffiliateEarnings.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const closeReverseDialog = () => {
    setReverseTarget(null);
    setReverseNote('');
  };

  const handleReverse = async () => {
    if (!reverseTarget) return;
    try {
      await reverseEarning({ variables: { id: reverseTarget.id, note: reverseNote.trim() || null } });
      toast({ title: t('admin.affiliateEarnings.reversed') });
      closeReverseDialog();
      refetch();
    } catch (err) {
      toast({
        title: t('admin.common.error'),
        description: err instanceof Error ? err.message : '',
        variant: 'destructive',
      });
    }
  };

  const statusLabel: Record<string, string> = {
    pending: t('admin.affiliateEarnings.statusPending'),
    available: t('admin.affiliateEarnings.statusAvailable'),
    reversed: t('admin.affiliateEarnings.statusReversed'),
  };
  const statusVariant: Record<string, 'secondary' | 'default' | 'destructive'> = {
    pending: 'secondary',
    available: 'default',
    reversed: 'destructive',
  };
  const sourceLabel: Record<string, string> = {
    payment: t('admin.affiliateEarnings.sourcePayment'),
    course: t('admin.affiliateEarnings.sourceCourse'),
    cpm: t('admin.affiliateEarnings.sourceCpm'),
  };
  const kindLabel: Record<'affiliate' | 'producer', string> = {
    affiliate: t('admin.affiliateEarnings.kindAffiliate'),
    producer: t('admin.affiliateEarnings.kindProducer'),
  };

  return (
    <Layout>
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
        <div className="max-w-6xl mx-auto space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" /> {t('admin.affiliateEarnings.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <Input
                  placeholder={t('admin.affiliateEarnings.searchPlaceholder')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="sm:max-w-sm"
                />
                <Select
                  value={kindFilter}
                  onValueChange={(value: CommissionKindFilter) => {
                    setKindFilter(value);
                    setPage(1);
                  }}
                >
                  <SelectTrigger
                    aria-label={t('admin.affiliateEarnings.kindLabel')}
                    className="w-full sm:w-48"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {t('admin.affiliateEarnings.kindAll')}
                    </SelectItem>
                    <SelectItem value="affiliate">
                      {t('admin.affiliateEarnings.kindAffiliate')}
                    </SelectItem>
                    <SelectItem value="producer">
                      {t('admin.affiliateEarnings.kindProducer')}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {error ? (
                <div
                  role="alert"
                  className="flex flex-col items-center gap-3 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-8 text-center"
                >
                  <AlertCircle aria-hidden="true" className="h-5 w-5 text-destructive" />
                  <p className="text-sm text-muted-foreground">
                    {t('admin.affiliateEarnings.loadError')}
                  </p>
                  <Button variant="outline" size="sm" onClick={() => void refetch()}>
                    {t('admin.affiliateEarnings.retry')}
                  </Button>
                </div>
              ) : loading && rows.length === 0 ? (
                <div
                  role="status"
                  aria-live="polite"
                  className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground"
                >
                  <LoaderCircle aria-hidden="true" className="h-4 w-4 animate-spin" />
                  {t('admin.affiliateEarnings.loading')}
                </div>
              ) : rows.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">
                  {t('admin.affiliateEarnings.empty')}
                </p>
              ) : (
                <>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('admin.affiliateEarnings.date')}</TableHead>
                          <TableHead>{t('admin.affiliateEarnings.recipient')}</TableHead>
                          <TableHead>{t('admin.affiliateEarnings.counterparty')}</TableHead>
                          <TableHead>{t('admin.affiliateEarnings.payment')}</TableHead>
                          <TableHead>{t('admin.affiliateEarnings.commission')}</TableHead>
                          <TableHead>{t('admin.affiliateEarnings.provider')}</TableHead>
                          <TableHead>{t('admin.affiliateEarnings.status')}</TableHead>
                          <TableHead className="text-right">{t('admin.affiliateEarnings.actions')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {rows.map((e) => {
                          const display = deriveEarningDisplayStatus({
                            status: e.status,
                            paymentDate: e.payment_date ? new Date(e.payment_date) : null,
                            createdAt: new Date(e.createdAt),
                            now: new Date(),
                          });
                          return (
                            <TableRow key={e.id}>
                              <TableCell>
                                {new Date(e.payment_date || e.createdAt).toLocaleDateString(i18n.language)}
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="text-sm font-medium">{e.user_name || '—'}</span>
                                  <Badge variant={e.kind === 'producer' ? 'secondary' : 'outline'}>
                                    {e.kind === 'producer'
                                      ? kindLabel.producer
                                      : kindLabel.affiliate}
                                  </Badge>
                                </div>
                                <div className="text-xs text-muted-foreground">{e.user_email}</div>
                              </TableCell>
                              <TableCell>
                                {e.kind === 'producer' ? (
                                  <div className="text-sm font-medium">{e.course_title || '—'}</div>
                                ) : (
                                  <>
                                    <div className="text-sm font-medium">{e.referred_user_name || '—'}</div>
                                    <div className="text-xs text-muted-foreground">{e.referred_user_email}</div>
                                  </>
                                )}
                              </TableCell>
                              <TableCell>
                                {e.source === 'cpm' ? '—' : formatCents(e.payment_amount_cents, 'BRL')}
                              </TableCell>
                              <TableCell className="font-medium">{formatCents(e.amount_cents, 'BRL')}</TableCell>
                              <TableCell>
                                {sourceLabel[e.source] ?? e.source}
                                {e.source !== 'cpm' && e.payment_provider ? (
                                  <span className="text-muted-foreground text-xs ml-1">
                                    ({e.payment_provider})
                                  </span>
                                ) : null}
                              </TableCell>
                              <TableCell>
                                <Badge variant={statusVariant[display]}>{statusLabel[display]}</Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                {e.status !== 'reversed' && (
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => setReverseTarget(e)}
                                  >
                                    {t('admin.affiliateEarnings.reverse')}
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">
                      {t('admin.affiliateEarnings.total', { total })}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        aria-label={t('admin.affiliateEarnings.previousPage')}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page <= 1}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="text-sm">{page} / {pageCount}</span>
                      <Button
                        variant="outline"
                        size="icon"
                        aria-label={t('admin.affiliateEarnings.nextPage')}
                        onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                        disabled={page >= pageCount}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      <Dialog
        open={!!reverseTarget}
        onOpenChange={(open) => {
          if (!open) closeReverseDialog();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.affiliateEarnings.reverseTitle')}</DialogTitle>
            <DialogDescription>{t('admin.affiliateEarnings.reverseDescription')}</DialogDescription>
          </DialogHeader>
          <Input
            placeholder={t('admin.affiliateEarnings.reverseNotePlaceholder')}
            value={reverseNote}
            onChange={(e) => setReverseNote(e.target.value)}
          />
          <DialogFooter>
            <Button variant="outline" onClick={closeReverseDialog}>
              {t('admin.affiliateEarnings.cancel')}
            </Button>
            <Button variant="destructive" disabled={reversing} onClick={handleReverse}>
              {t('admin.affiliateEarnings.confirmReverse')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default AdminAffiliateEarnings;
